import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import Tesseract from "tesseract.js";
import mammoth from "mammoth";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { getCurrentUserCompanyId, getCurrentUserData } from "../utils/userUtils";
import { hasPermission } from "../utils/permissions";
import { SPLIT_WORKFLOW_TYPE } from "../utils/workflowUtils";
import FormAutomationPresetEditor from "./FormAutomationPresetEditor";
import {
  AI_IMPORT_PRESETS,
  formatStarterFieldTemplates,
  getBuiltInPreset,
  parseStarterFieldTemplates,
  resolvePresetSelection,
} from "../utils/formAutomationPresets";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version || "5.4.394"}/legacy/build/pdf.worker.min.mjs`;

const AI_OPTION_COLORS = ["#fee2e2", "#dbeafe", "#dcfce7", "#fef3c7", "#ede9fe", "#fce7f3"];

async function extractPdfPageText(page) {
  try {
    const textContent = await page.getTextContent();
    return (textContent?.items || [])
      .map((item) => ("str" in item ? item.str : ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  } catch (error) {
    return "";
  }
}

async function extractPdfPageLayout(page) {
  try {
    const textContent = await page.getTextContent();
    return (textContent?.items || [])
      .map((item) => ({
        text: String("str" in item ? item.str : "").replace(/\s+/g, " ").trim(),
        x: Number(item?.transform?.[4] || 0),
        y: Number(item?.transform?.[5] || 0),
        width: Number(item?.width || 0),
        height: Number(item?.height || 0),
      }))
      .filter((item) => item.text);
  } catch (error) {
    return [];
  }
}

async function extractPdfPageWithOCR(page, pageNumber, totalPages, setUploadProgress, setUploadStage) {
  const viewport = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas rendering is not available in this browser.");
  }

  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: context, viewport }).promise;

  const ocrResult = await Tesseract.recognize(canvas, "eng", {
    logger: (message) => {
      if (message.status === "recognizing text") {
        setUploadProgress(Math.round(message.progress * 100));
        setUploadStage(`Running OCR on page ${pageNumber} of ${totalPages}...`);
      }
    },
  });

  return String(ocrResult?.data?.text || "").trim();
}

function mergeStarterSectionsIntoDraft(draft = {}, starterSections = []) {
  const requestedSections = (Array.isArray(starterSections) ? starterSections : [])
    .map((section) => String(section || "").trim())
    .filter(Boolean);

  if (requestedSections.length === 0) {
    return draft;
  }

  const existingSections = Array.isArray(draft?.sections) ? draft.sections : [];
  const existingTitles = new Set(existingSections.map((section) => String(section?.title || "").trim().toLowerCase()).filter(Boolean));
  const appendedSections = requestedSections
    .filter((title) => !existingTitles.has(title.toLowerCase()))
    .map((title) => ({
      title,
      description: "",
      assignmentHint: "",
      repeatable: false,
      splitWorkflow: false,
      workflowVisibility: "visible",
      fields: [createEmptyDraftField("text")],
    }));

  return {
    ...draft,
    sections: [...existingSections, ...appendedSections],
  };
}

function mergeStarterFieldTemplatesIntoDraft(draft = {}, starterFieldTemplates = []) {
  const templates = Array.isArray(starterFieldTemplates) ? starterFieldTemplates : [];
  if (templates.length === 0) {
    return draft;
  }

  const sections = Array.isArray(draft?.sections) ? [...draft.sections] : [];

  templates.forEach((template) => {
    const sectionTitle = String(template?.sectionTitle || "").trim();
    const fields = Array.isArray(template?.fields) ? template.fields : [];
    if (!sectionTitle || fields.length === 0) return;

    let sectionIndex = sections.findIndex((section) => String(section?.title || "").trim().toLowerCase() === sectionTitle.toLowerCase());
    if (sectionIndex < 0) {
      sections.push({
        title: sectionTitle,
        description: "",
        assignmentHint: "",
        repeatable: false,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: [],
      });
      sectionIndex = sections.length - 1;
    }

    const existingFieldLabels = new Set(
      (Array.isArray(sections[sectionIndex].fields) ? sections[sectionIndex].fields : [])
        .map((field) => String(field?.label || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const appendedFields = fields
      .map((field) => String(field || "").trim())
      .filter(Boolean)
      .filter((label) => !existingFieldLabels.has(label.toLowerCase()))
      .map((label) => createEmptyDraftField("text", label));

    sections[sectionIndex] = {
      ...sections[sectionIndex],
      fields: [...(sections[sectionIndex].fields || []), ...appendedFields],
    };
  });

  return {
    ...draft,
    sections,
  };
}

function makeBuilderFieldId(prefix = "field") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function buildBasicFieldsFromText(text) {
  let lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 1) {
    lines = lines[0]
      .split(/(?<=[?.!])\s+|\.{2,}|-+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  return lines.map((line) => ({
    id: makeBuilderFieldId("field"),
    type: "text",
    label: line.replace(/^[-*]\s*/, ""),
    required: false,
    config: { searchable: false },
    branchMap: {},
    attachments: [],
  }));
}

function inferScoreFromLabel(label) {
  const text = String(label || "").trim().toLowerCase();
  if (!text) return 0;

  const positivePatterns = [
    "yes", "pass", "passed", "compliant", "complete", "completed", "ok", "good", "safe", "present", "checked",
  ];
  const negativePatterns = [
    "no", "fail", "failed", "non-compliant", "non compliant", "not ok", "unsafe", "missing", "unchecked",
  ];
  const neutralPatterns = ["n/a", "na", "not applicable", "other"];

  if (neutralPatterns.some((pattern) => text === pattern)) return 0;
  if (positivePatterns.some((pattern) => text.includes(pattern))) return 1;
  if (negativePatterns.some((pattern) => text.includes(pattern))) return 0;
  return 0;
}

function buildMultipleChoiceOptions(options = []) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (typeof option === "string") {
        const label = String(option || "").trim();
        return label ? { label, score: inferScoreFromLabel(label) } : null;
      }

      const label = String(option?.label || "").trim();
      if (!label) return null;

      const parsedScore = Number(option?.score);
      return {
        label,
        score: Number.isFinite(parsedScore) ? parsedScore : inferScoreFromLabel(label),
      };
    })
    .filter(Boolean)
    .slice(0, 6)
    .map((option, index) => ({
      id: makeBuilderFieldId("opt"),
      label: option.label,
      score: option.score,
      color: AI_OPTION_COLORS[index % AI_OPTION_COLORS.length],
    }));
}

function buildEvidenceFollowUpFields(field, parentLabel) {
  const evidence = String(field?.evidence || "none").trim();
  const followUps = [];

  if (evidence === "photo" || evidence === "photo_and_signature") {
    followUps.push({
      id: makeBuilderFieldId("media"),
      type: "media",
      label: `${parentLabel} photo evidence`,
      required: true,
      branchMap: {},
      attachments: [],
      config: { allowMultiple: true, files: [], searchable: false },
    });
  }

  if (evidence === "signature" || evidence === "photo_and_signature") {
    followUps.push({
      id: makeBuilderFieldId("signature"),
      type: "signature",
      label: `${parentLabel} sign-off`,
      required: true,
      branchMap: {},
      attachments: [],
      config: { requireTypedName: true, searchable: false },
    });
  }

  return followUps;
}

function mapDraftFieldToBuilder(field, sectionTitle = "") {
  const requestedType = String(field?.type || "text").trim();
  const allowedTypes = new Set([
    "instruction",
    "yesno",
    "text",
    "number",
    "date_time",
    "multiple_choice",
    "checkbox",
    "media",
    "signature",
    "location",
  ]);
  const type = allowedTypes.has(requestedType) ? requestedType : "text";
  const label = String(field?.label || "").trim() || `${sectionTitle || "Field"} item`;
  const helpText = String(field?.helpText || "").trim();
  const required = !!field?.required;
  const evidenceFields = buildEvidenceFollowUpFields(field, label);

  const base = {
    id: makeBuilderFieldId(type),
    type,
    label,
    required,
    branchMap: {},
    attachments: [],
  };

  switch (type) {
    case "instruction":
      return [{
        ...base,
        required: false,
        config: {
          text: helpText || label,
          attachments: [],
          searchable: false,
        },
      }];
    case "multiple_choice":
      return [{
        ...base,
        config: {
          options: buildMultipleChoiceOptions(field?.options),
          allowOther: false,
          multi: false,
          searchable: false,
        },
      }, ...evidenceFields];
    case "number":
      return [{
        ...base,
        config: { min: null, max: null, step: 1, searchable: false, unit: "none" },
      }, ...evidenceFields];
    case "date_time":
      return [{
        ...base,
        config: { mode: "datetime", searchable: false },
      }, ...evidenceFields];
    case "media":
      return [{
        ...base,
        config: { allowMultiple: true, files: [], searchable: false },
      }];
    case "signature":
      return [{
        ...base,
        config: { requireTypedName: true, searchable: false },
      }];
    case "location":
      return [{
        ...base,
        config: { mode: "manual", searchable: false },
      }, ...evidenceFields];
    case "yesno":
      return [{
        ...base,
        config: { yesColor: "#22c55e", noColor: "#ef4444", searchable: false },
      }, ...evidenceFields];
    case "checkbox":
      return [{
        ...base,
        config: { searchable: false },
      }, ...evidenceFields];
    case "text":
    default:
      return [{
        ...base,
        type: "text",
        config: { searchable: false },
      }, ...evidenceFields];
  }
}

function buildFormDataFromAIDraft(draft, fallbackTitle) {
  const title = String(draft?.title || "").trim() || fallbackTitle;
  const description = String(draft?.description || "").trim();
  const sections = Array.isArray(draft?.sections) ? draft.sections : [];
  const standaloneFields = Array.isArray(draft?.standaloneFields) ? draft.standaloneFields : [];
  const fields = [];

  if (description) {
    fields.push({
      id: makeBuilderFieldId("instruction"),
      type: "instruction",
      label: "Overview",
      required: false,
      branchMap: {},
      attachments: [],
      config: {
        text: description,
        attachments: [],
        searchable: false,
      },
    });
  }

  standaloneFields.forEach((field) => {
    fields.push(...mapDraftFieldToBuilder(field));
  });

  sections.forEach((section, index) => {
    const sectionTitle = String(section?.title || "").trim() || `Section ${index + 1}`;
    const sectionDescription = String(section?.description || "").trim();
    const assignmentHint = String(section?.assignmentHint || "").trim();
    const sectionRepeatable = !!section?.repeatable;
    const splitWorkflow = !!section?.splitWorkflow;
    const workflowVisibility = String(section?.workflowVisibility || "visible").trim() === "hidden" ? "hidden" : "visible";
    const sectionFields = (Array.isArray(section?.fields) ? section.fields : [])
      .flatMap((field) => mapDraftFieldToBuilder(field, sectionTitle));

    if (sectionFields.length === 0) return;

    const instructionTextParts = [];
    if (sectionDescription) instructionTextParts.push(sectionDescription);
    if (splitWorkflow && assignmentHint) {
      instructionTextParts.push(`AI suggestion: assign this workflow section to ${assignmentHint}. Review and set the real users or groups in field properties.`);
    }
    if (splitWorkflow) {
      instructionTextParts.push(
        workflowVisibility === "hidden"
          ? "AI suggestion: later assignees should not see previous section responses."
          : "AI suggestion: later assignees may view previous section responses."
      );
    }

    const children = instructionTextParts.length > 0
      ? [
          {
            id: makeBuilderFieldId("instruction"),
            type: "instruction",
            label: `${sectionTitle} instructions`,
            required: false,
            branchMap: {},
            attachments: [],
            config: {
              text: instructionTextParts.join("\n\n"),
              attachments: [],
              searchable: false,
            },
          },
          ...sectionFields,
        ]
      : sectionFields;

    fields.push({
      id: makeBuilderFieldId(splitWorkflow ? "split_workflow" : "section"),
      type: splitWorkflow ? SPLIT_WORKFLOW_TYPE : "section",
      label: sectionTitle,
      required: false,
      branchMap: {},
      attachments: [],
      config: splitWorkflow
        ? {
            repeatable: sectionRepeatable,
            assignedUsers: [],
            assignedGroups: [],
            allUsers: true,
            allGroups: false,
            previousSectionVisibility: workflowVisibility,
          }
        : { repeatable: sectionRepeatable },
      children,
    });
  });

  return {
    title,
    fields: fields.length > 0 ? fields : buildBasicFieldsFromText(title),
  };
}

function countDraftFields(draft = {}) {
  const standaloneFields = Array.isArray(draft?.standaloneFields) ? draft.standaloneFields.length : 0;
  const sectionFields = (Array.isArray(draft?.sections) ? draft.sections : []).reduce((total, section) => {
    return total + (Array.isArray(section?.fields) ? section.fields.length : 0);
  }, 0);
  return standaloneFields + sectionFields;
}

function getDraftTypeLabel(type) {
  const labels = {
    instruction: "Instruction",
    yesno: "Yes / No",
    text: "Text",
    number: "Number",
    date_time: "Date & Time",
    multiple_choice: "Multiple Choice",
    checkbox: "Checkbox",
    media: "Media",
    signature: "Signature",
    location: "Location",
  };
  return labels[type] || "Text";
}

function cloneDraftForEditing(draft = {}) {
  return {
    title: String(draft?.title || ""),
    description: String(draft?.description || ""),
    standaloneFields: Array.isArray(draft?.standaloneFields)
      ? draft.standaloneFields.map((field) => ({
          ...field,
          options: Array.isArray(field?.options)
            ? field.options.map((option) => (typeof option === "string" ? { label: option, score: inferScoreFromLabel(option) } : { ...option }))
            : [],
        }))
      : [],
    sections: Array.isArray(draft?.sections)
      ? draft.sections.map((section) => ({
          ...section,
          fields: Array.isArray(section?.fields)
            ? section.fields.map((field) => ({
                ...field,
                options: Array.isArray(field?.options)
                  ? field.options.map((option) => (typeof option === "string" ? { label: option, score: inferScoreFromLabel(option) } : { ...option }))
                  : [],
              }))
            : [],
        }))
      : [],
  };
}

function createEmptyDraftField(type = "text", label = "") {
  return {
    type,
    label,
    required: false,
    helpText: "",
    evidence: "none",
    options: type === "multiple_choice" ? [{ label: "Option 1", score: 0 }, { label: "Option 2", score: 0 }] : [],
  };
}

function toDisplayTitle(value = "") {
  return String(value || "")
    .replace(/\.[^/.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function createDraftTemplateField(type, label, options = {}) {
  return {
    type,
    label,
    required: !!options.required,
    helpText: options.helpText || "",
    evidence: options.evidence || "none",
    options: Array.isArray(options.options) ? options.options : [],
  };
}

function getNormalizedTemplateMetadata(sourceText = "", fileName = "") {
  const normalizedText = String(sourceText || "").toLowerCase().replace(/\s+/g, " ").trim();
  const normalizedFileName = String(fileName || "").toLowerCase();
  const weekdayMatches = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
    .filter((day) => normalizedText.includes(day));

  return {
    normalizedText,
    normalizedFileName,
    weekdayMatches,
  };
}

function inferTemplateFieldTypeFromLabel(label = "") {
  const value = String(label || "").trim().toLowerCase();
  if (!value) return "text";
  if (["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].includes(value)) return "checkbox";
  if (/(date|time)/.test(value)) return "date_time";
  if (/(temp|temperature|reading|value|score|quantity|qty|amount)/.test(value)) return "number";
  if (/(pass|fail|result|status|rating|outcome)/.test(value)) return "multiple_choice";
  if (/(signature|sign off|sign-off|approved by)/.test(value)) return "signature";
  if (/(location|area|site|room)/.test(value)) return "location";
  if (/(photo|image)/.test(value)) return "media";
  if (/(notes|comments|comment|details|description|action)/.test(value)) return "text";
  return "text";
}

function getTemplateFieldOptions(type, label = "") {
  if (type !== "multiple_choice") return [];
  const value = String(label || "").trim().toLowerCase();
  if (/(pass|fail|result|outcome)/.test(value)) {
    return [
      { label: "Pass", score: 1 },
      { label: "Fail", score: 0 },
      { label: "N/A", score: 0 },
    ];
  }
  return [
    { label: "Yes", score: 1 },
    { label: "No", score: 0 },
    { label: "N/A", score: 0 },
  ];
}

function extractTemplateHeadingsFromLayout(layoutItems = []) {
  const seen = new Set();
  return (Array.isArray(layoutItems) ? layoutItems : [])
    .filter((item) => item?.text)
    .filter((item) => item.height <= 16)
    .filter((item) => item.width <= 220)
    .filter((item) => item.text.split(/\s+/).length <= 4)
    .filter((item) => !/(template|checklist template)/i.test(item.text))
    .filter((item) => !/^[\W_]+$/.test(item.text))
    .sort((a, b) => {
      if (Math.abs(b.y - a.y) > 6) return b.y - a.y;
      return a.x - b.x;
    })
    .map((item) => item.text)
    .filter((label) => {
      const key = label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 12);
}

function buildLayoutTemplateDraft(fallbackTitle = "Generated Template", headingLabels = []) {
  const title = toDisplayTitle(fallbackTitle) || "Generated Template";
  const labels = (Array.isArray(headingLabels) ? headingLabels : [])
    .map((label) => String(label || "").trim())
    .filter(Boolean);

  if (labels.length < 3) {
    return null;
  }

  const standaloneFields = [];
  const sectionFields = [];

  labels.forEach((label) => {
    const type = inferTemplateFieldTypeFromLabel(label);
    const field = createDraftTemplateField(type, label, {
      required: type !== "checkbox",
      options: getTemplateFieldOptions(type, label),
    });

    if (type === "date_time" && standaloneFields.length < 2) {
      standaloneFields.push(field);
      return;
    }

    sectionFields.push(field);
  });

  if (sectionFields.length === 0) {
    return null;
  }

  return {
    title,
    description: "Template detected from visible PDF headings. Review the inferred columns before opening the form builder.",
    standaloneFields,
    sections: [
      {
        title: "Template rows",
        description: "Each repeatable row represents one line or record from the original PDF template.",
        assignmentHint: "",
        repeatable: true,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: sectionFields,
      },
    ],
  };
}

function detectWeeklyChecklistTemplate(metadata) {
  const { normalizedText, normalizedFileName, weekdayMatches } = metadata;

  return (
    weekdayMatches.length >= 5 &&
    normalizedText.includes("start date") &&
    normalizedText.includes("notes") &&
    normalizedText.length <= 250 &&
    (normalizedText.includes("weekly checklist") || normalizedFileName.includes("weekly"))
  );
}

function buildWeeklyChecklistTemplateDraft(fallbackTitle = "Weekly Checklist") {
  const title = toDisplayTitle(fallbackTitle) || "Weekly Checklist";

  return {
    title,
    description: "Template detected from a weekly checklist PDF. Add one repeatable row per task and tick each day as work is completed through the week.",
    standaloneFields: [
      createDraftTemplateField("date_time", "Start date", {
        required: true,
        helpText: "Choose the first day for this weekly checklist.",
      }),
    ],
    sections: [
      {
        title: "Weekly tasks",
        description: "Add each recurring task as its own row, then mark the days it was completed.",
        assignmentHint: "",
        repeatable: true,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: [
          createDraftTemplateField("text", "Task description", {
            required: true,
            helpText: "Describe the task or check that must be completed during the week.",
          }),
          ...["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => createDraftTemplateField("checkbox", day)),
          createDraftTemplateField("text", "Notes", {
            helpText: "Optional notes for this weekly task row.",
          }),
        ],
      },
    ],
  };
}

function buildTemperatureLogTemplateDraft(fallbackTitle = "Temperature Log") {
  const title = toDisplayTitle(fallbackTitle) || "Temperature Log";

  return {
    title,
    description: "Template detected from a temperature log PDF. Add one repeatable row per check and record the measured temperature, location, and any corrective action.",
    standaloneFields: [
      createDraftTemplateField("date_time", "Log date", {
        required: true,
        helpText: "Choose the date for this temperature log.",
      }),
      createDraftTemplateField("text", "Completed by", {
        helpText: "Optional staff member or department completing the log.",
      }),
    ],
    sections: [
      {
        title: "Temperature checks",
        description: "Create one row per fridge, freezer, room, or product check.",
        assignmentHint: "",
        repeatable: true,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: [
          createDraftTemplateField("text", "Item or area", {
            required: true,
            helpText: "Fridge, freezer, room, or product being checked.",
          }),
          createDraftTemplateField("number", "Temperature reading", {
            required: true,
            helpText: "Enter the measured temperature.",
          }),
          createDraftTemplateField("date_time", "Time checked", {
            required: true,
          }),
          createDraftTemplateField("yesno", "Within acceptable range?", {
            required: true,
          }),
          createDraftTemplateField("text", "Corrective action", {
            helpText: "Record any action taken when the reading is out of range.",
          }),
          createDraftTemplateField("signature", "Checked by sign-off", {
            helpText: "Optional sign-off for this individual reading.",
          }),
        ],
      },
    ],
  };
}

function buildCleaningChecklistTemplateDraft(fallbackTitle = "Cleaning Checklist") {
  const title = toDisplayTitle(fallbackTitle) || "Cleaning Checklist";

  return {
    title,
    description: "Template detected from a cleaning checklist PDF. Add one repeatable row per area or task, then record completion, notes, and sign-off.",
    standaloneFields: [
      createDraftTemplateField("date_time", "Checklist date", {
        required: true,
      }),
      createDraftTemplateField("text", "Checked by", {
        helpText: "Optional person or team completing this cleaning checklist.",
      }),
    ],
    sections: [
      {
        title: "Cleaning tasks",
        description: "Create one row per area, item, or cleaning task that needs to be checked.",
        assignmentHint: "",
        repeatable: true,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: [
          createDraftTemplateField("text", "Area or task", {
            required: true,
          }),
          createDraftTemplateField("yesno", "Completed?", {
            required: true,
          }),
          createDraftTemplateField("media", "Photo evidence", {
            helpText: "Optional photo of completed cleaning where needed.",
          }),
          createDraftTemplateField("text", "Comments", {
            helpText: "Notes, exceptions, or follow-up actions.",
          }),
          createDraftTemplateField("signature", "Supervisor sign-off"),
        ],
      },
    ],
  };
}

function buildAuditChecklistTemplateDraft(fallbackTitle = "Audit Checklist") {
  const title = toDisplayTitle(fallbackTitle) || "Audit Checklist";

  return {
    title,
    description: "Template detected from an audit or inspection PDF. Add one repeatable row per point being assessed, then record compliance, evidence, and corrective action.",
    standaloneFields: [
      createDraftTemplateField("date_time", "Inspection date", {
        required: true,
      }),
      createDraftTemplateField("text", "Inspector name", {
        required: true,
      }),
      createDraftTemplateField("location", "Inspection location"),
    ],
    sections: [
      {
        title: "Audit points",
        description: "Create one row per question, standard, or checkpoint.",
        assignmentHint: "",
        repeatable: true,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: [
          createDraftTemplateField("text", "Audit point", {
            required: true,
          }),
          createDraftTemplateField("multiple_choice", "Result", {
            required: true,
            options: [
              { label: "Pass", score: 1 },
              { label: "Fail", score: 0 },
              { label: "N/A", score: 0 },
            ],
          }),
          createDraftTemplateField("media", "Evidence photo"),
          createDraftTemplateField("text", "Comments"),
          createDraftTemplateField("text", "Corrective action"),
        ],
      },
      {
        title: "Final sign-off",
        description: "Record final outcome and sign-off after the audit is reviewed.",
        assignmentHint: "",
        repeatable: false,
        splitWorkflow: false,
        workflowVisibility: "visible",
        fields: [
          createDraftTemplateField("signature", "Inspector signature", {
            required: true,
          }),
          createDraftTemplateField("signature", "Manager sign-off"),
        ],
      },
    ],
  };
}

function resolveLowTextTemplateDraft(sourceText = "", fileName = "", layoutItems = []) {
  const metadata = getNormalizedTemplateMetadata(sourceText, fileName);
  const { normalizedText, normalizedFileName } = metadata;

  if (detectWeeklyChecklistTemplate(metadata)) {
    return {
      draft: buildWeeklyChecklistTemplateDraft(fileName),
      presetKey: "checklist",
      presetLabel: "Checklist",
      extractionHint: "This PDF only exposed the weekly template labels, so BuildMyChecks generated a structured weekly task matrix instead of guessing from missing text.",
    };
  }

  if (
    normalizedText.length <= 350 &&
    (normalizedText.includes("temperature") || normalizedText.includes("temp")) &&
    (normalizedText.includes("log") || normalizedFileName.includes("temp"))
  ) {
    return {
      draft: buildTemperatureLogTemplateDraft(fileName),
      presetKey: "food_safety",
      presetLabel: "Food Safety",
      extractionHint: "This PDF exposed only temperature log labels, so BuildMyChecks created a structured temperature log draft for review.",
    };
  }

  if (
    normalizedText.length <= 350 &&
    (normalizedText.includes("cleaning") || normalizedText.includes("clean")) &&
    (normalizedText.includes("checklist") || normalizedText.includes("schedule") || normalizedText.includes("task"))
  ) {
    return {
      draft: buildCleaningChecklistTemplateDraft(fileName),
      presetKey: "cleaning",
      presetLabel: "Cleaning",
      extractionHint: "This PDF exposed only cleaning checklist labels, so BuildMyChecks created a repeatable cleaning task draft for review.",
    };
  }

  if (
    normalizedText.length <= 400 &&
    (normalizedText.includes("audit") || normalizedText.includes("inspection")) &&
    (normalizedText.includes("checklist") || normalizedText.includes("template") || normalizedText.includes("notes"))
  ) {
    return {
      draft: buildAuditChecklistTemplateDraft(fileName),
      presetKey: "audit",
      presetLabel: "Audit",
      extractionHint: "This PDF exposed only audit or inspection headings, so BuildMyChecks created a structured audit checklist draft for review.",
    };
  }

  if (normalizedText.length <= 450) {
    const headingLabels = extractTemplateHeadingsFromLayout(layoutItems);
    const layoutDraft = buildLayoutTemplateDraft(fileName, headingLabels);
    if (layoutDraft) {
      return {
        draft: layoutDraft,
        presetKey: "general",
        presetLabel: "General",
        extractionHint: `This PDF exposed only sparse text, so BuildMyChecks inferred a repeatable row template from visible headings: ${headingLabels.join(", ")}.`,
      };
    }
  }

  return null;
}

function createEmptyDraftSection() {
  return {
    title: "",
    description: "",
    assignmentHint: "",
    repeatable: false,
    splitWorkflow: false,
    workflowVisibility: "visible",
    fields: [createEmptyDraftField("text")],
  };
}

function moveArrayItem(items = [], fromIndex, toIndex) {
  const list = [...items];
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }

  const [moved] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, moved);
  return list;
}

const NewFormModal = ({ isOpen, onClose, onScratch, onDocumentUpload }) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [uploadStage, setUploadStage] = useState("");
  const [currentUserData, setCurrentUserData] = useState(null);
  const [draftReview, setDraftReview] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState("general");
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [companyDefaultPreset, setCompanyDefaultPreset] = useState("general");
  const [customPresets, setCustomPresets] = useState([]);
  const [loadingPresetSettings, setLoadingPresetSettings] = useState(false);
  const [savingPresetSettings, setSavingPresetSettings] = useState(false);
  const [presetManagerOpen, setPresetManagerOpen] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [presetForm, setPresetForm] = useState({
    name: "",
    basePresetKey: "general",
    extraInstructions: "",
    starterSections: "",
    starterFieldTemplates: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const ud = await getCurrentUserData();
        if (!mounted) return;
        setCurrentUserData(ud || null);
      } catch (error) {
        console.error("Failed to load user for NewFormModal", error);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDraftReview(null);
      setUploadError("");
      setUploadStage("");
      setUploadProgress(0);
      setUploading(false);
      setDragItem(null);
      setSelectedPreset("general");
      setCurrentCompanyId(null);
      setCompanyDefaultPreset("general");
      setCustomPresets([]);
      setLoadingPresetSettings(false);
      setSavingPresetSettings(false);
      setPresetManagerOpen(false);
      setEditingPresetId(null);
      setPresetForm({
        name: "",
        basePresetKey: "general",
        extraInstructions: "",
        starterSections: "",
        starterFieldTemplates: "",
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !currentUserData || !hasPermission(currentUserData, "create_forms")) return;

    let active = true;
    (async () => {
      try {
        setLoadingPresetSettings(true);
        const companyId = await getCurrentUserCompanyId();
        if (!active || !companyId) return;
        setCurrentCompanyId(companyId);
        const getSettings = httpsCallable(functions, "getFormAutomationSettings");
        const result = await getSettings({ companyId });
        const defaultPresetSelection = result?.data?.defaultPresetSelection || result?.data?.defaultPresetKey || "general";
        if (!active) return;
        setCompanyDefaultPreset(defaultPresetSelection);
        setSelectedPreset(defaultPresetSelection);
        setCustomPresets(Array.isArray(result?.data?.customPresets) ? result.data.customPresets : []);
      } catch (error) {
        console.error("Failed to load form automation settings:", error);
      } finally {
        if (active) {
          setLoadingPresetSettings(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [isOpen, currentUserData]);

  const submitBasicDraft = (title, sourceText) => {
    onDocumentUpload({
      title,
      fields: buildBasicFieldsFromText(sourceText),
    });
  };

  const submitReviewedAIDraft = () => {
    if (!draftReview?.draft || !draftReview?.fallbackTitle) return;
    onDocumentUpload(buildFormDataFromAIDraft(draftReview.draft, draftReview.fallbackTitle));
  };

  const saveCompanyDefaultPreset = async () => {
    if (!currentCompanyId) return;
    try {
      setSavingPresetSettings(true);
      const setSettings = httpsCallable(functions, "setFormAutomationSettings");
      const resolved = resolvePresetSelection(selectedPreset, customPresets);
      const result = await setSettings({
        companyId: currentCompanyId,
        defaultPresetKey: resolved.presetKey,
        defaultPresetSelection: selectedPreset,
      });
      setCompanyDefaultPreset(result?.data?.defaultPresetSelection || selectedPreset);
    } catch (error) {
      console.error("Failed to save form automation settings:", error);
      alert(error.message || "Could not save company default preset.");
    } finally {
      setSavingPresetSettings(false);
    }
  };

  const openPresetManagerForCreate = () => {
    const resolved = resolvePresetSelection(selectedPreset, customPresets);
    setEditingPresetId(null);
    setPresetForm({
      name: resolved.customPresetName || resolved.label || "",
      basePresetKey: resolved.presetKey,
      extraInstructions: resolved.customPresetInstructions || "",
      starterSections: (resolved.customPresetStarterSections || []).join(", "),
      starterFieldTemplates: formatStarterFieldTemplates(resolved.customPresetStarterFieldTemplates || []),
    });
    setPresetManagerOpen(true);
  };

  const openPresetManagerForEdit = (preset) => {
    setEditingPresetId(preset.id);
    setPresetForm({
      name: preset.name || "",
      basePresetKey: preset.basePresetKey || "general",
      extraInstructions: preset.extraInstructions || "",
      starterSections: (preset.starterSections || []).join(", "),
      starterFieldTemplates: formatStarterFieldTemplates(preset.starterFieldTemplates || []),
    });
    setPresetManagerOpen(true);
  };

  const closePresetManager = () => {
    setPresetManagerOpen(false);
    setEditingPresetId(null);
  };

  const saveCustomPreset = async () => {
    if (!currentCompanyId) return;
    const trimmedName = String(presetForm.name || "").trim();
    if (!trimmedName) {
      alert("Preset name is required.");
      return;
    }

    const starterSections = String(presetForm.starterSections || "")
      .split(",")
      .map((section) => section.trim())
      .filter(Boolean)
      .slice(0, 12);
    const starterFieldTemplates = parseStarterFieldTemplates(presetForm.starterFieldTemplates);

    const nextPreset = {
      id: editingPresetId || `preset_${Date.now()}`,
      name: trimmedName,
      basePresetKey: presetForm.basePresetKey || "general",
      extraInstructions: String(presetForm.extraInstructions || "").trim(),
      starterSections,
      starterFieldTemplates,
    };
    const nextCustomPresets = editingPresetId
      ? customPresets.map((preset) => (preset.id === editingPresetId ? nextPreset : preset))
      : [...customPresets, nextPreset];

    try {
      setSavingPresetSettings(true);
      const setSettings = httpsCallable(functions, "setFormAutomationSettings");
      await setSettings({
        companyId: currentCompanyId,
        defaultPresetKey: resolvePresetSelection(companyDefaultPreset, nextCustomPresets).presetKey,
        defaultPresetSelection: companyDefaultPreset,
        customPresets: nextCustomPresets,
      });
      setCustomPresets(nextCustomPresets);
      setSelectedPreset(`custom:${nextPreset.id}`);
      closePresetManager();
    } catch (error) {
      console.error("Failed to save custom company preset:", error);
      alert(error.message || "Could not save company preset.");
    } finally {
      setSavingPresetSettings(false);
    }
  };

  const deleteCustomPreset = async (presetId) => {
    if (!currentCompanyId) return;
    const nextCustomPresets = customPresets.filter((preset) => preset.id !== presetId);
    const nextDefaultSelection = companyDefaultPreset === `custom:${presetId}` ? "general" : companyDefaultPreset;
    const nextSelectedPreset = selectedPreset === `custom:${presetId}` ? "general" : selectedPreset;

    try {
      setSavingPresetSettings(true);
      const setSettings = httpsCallable(functions, "setFormAutomationSettings");
      await setSettings({
        companyId: currentCompanyId,
        defaultPresetKey: resolvePresetSelection(nextDefaultSelection, nextCustomPresets).presetKey,
        defaultPresetSelection: nextDefaultSelection,
        customPresets: nextCustomPresets,
      });
      setCustomPresets(nextCustomPresets);
      setCompanyDefaultPreset(nextDefaultSelection);
      setSelectedPreset(nextSelectedPreset);
    } catch (error) {
      console.error("Failed to delete custom company preset:", error);
      alert(error.message || "Could not delete company preset.");
    } finally {
      setSavingPresetSettings(false);
    }
  };

  const updateDraftReview = (updater) => {
    setDraftReview((current) => {
      if (!current?.draft) return current;
      const nextDraft = typeof updater === "function" ? updater(current.draft) : current.draft;
      return {
        ...current,
        draft: nextDraft,
      };
    });
  };

  const updateTopLevelField = (fieldIndex, patch) => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: (draft.standaloneFields || []).map((field, index) =>
        index === fieldIndex ? { ...field, ...patch } : field
      ),
    }));
  };

  const removeTopLevelField = (fieldIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: (draft.standaloneFields || []).filter((_, index) => index !== fieldIndex),
    }));
  };

  const addTopLevelField = (type = "text") => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: [...(draft.standaloneFields || []), createEmptyDraftField(type)],
    }));
  };

  const reorderTopLevelField = (fromIndex, toIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: moveArrayItem(draft.standaloneFields || [], fromIndex, toIndex),
    }));
  };

  const updateTopLevelFieldOption = (fieldIndex, optionIndex, patch) => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: (draft.standaloneFields || []).map((field, index) => {
        if (index !== fieldIndex) return field;
        return {
          ...field,
          options: (field.options || []).map((option, optIndex) =>
            optIndex === optionIndex ? { ...option, ...patch } : option
          ),
        };
      }),
    }));
  };

  const addTopLevelFieldOption = (fieldIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: (draft.standaloneFields || []).map((field, index) => {
        if (index !== fieldIndex) return field;
        return {
          ...field,
          options: [...(field.options || []), { label: "New option", score: 0 }],
        };
      }),
    }));
  };

  const removeTopLevelFieldOption = (fieldIndex, optionIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      standaloneFields: (draft.standaloneFields || []).map((field, index) => {
        if (index !== fieldIndex) return field;
        return {
          ...field,
          options: (field.options || []).filter((_, optIndex) => optIndex !== optionIndex),
        };
      }),
    }));
  };

  const updateSection = (sectionIndex, patch) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, index) =>
        index === sectionIndex ? { ...section, ...patch } : section
      ),
    }));
  };

  const removeSection = (sectionIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).filter((_, index) => index !== sectionIndex),
    }));
  };

  const addSection = () => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: [...(draft.sections || []), createEmptyDraftSection()],
    }));
  };

  const reorderSection = (fromIndex, toIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: moveArrayItem(draft.sections || [], fromIndex, toIndex),
    }));
  };

  const updateSectionField = (sectionIndex, fieldIndex, patch) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field, fIndex) =>
            fIndex === fieldIndex ? { ...field, ...patch } : field
          ),
        };
      }),
    }));
  };

  const removeSectionField = (sectionIndex, fieldIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: (section.fields || []).filter((_, fIndex) => fIndex !== fieldIndex),
        };
      }),
    }));
  };

  const addSectionField = (sectionIndex, type = "text") => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: [...(section.fields || []), createEmptyDraftField(type)],
        };
      }),
    }));
  };

  const reorderSectionField = (sectionIndex, fromIndex, toIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: moveArrayItem(section.fields || [], fromIndex, toIndex),
        };
      }),
    }));
  };

  const updateSectionFieldOption = (sectionIndex, fieldIndex, optionIndex, patch) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field, fIndex) => {
            if (fIndex !== fieldIndex) return field;
            return {
              ...field,
              options: (field.options || []).map((option, optIndex) =>
                optIndex === optionIndex ? { ...option, ...patch } : option
              ),
            };
          }),
        };
      }),
    }));
  };

  const addSectionFieldOption = (sectionIndex, fieldIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field, fIndex) => {
            if (fIndex !== fieldIndex) return field;
            return {
              ...field,
              options: [...(field.options || []), { label: "New option", score: 0 }],
            };
          }),
        };
      }),
    }));
  };

  const removeSectionFieldOption = (sectionIndex, fieldIndex, optionIndex) => {
    updateDraftReview((draft) => ({
      ...draft,
      sections: (draft.sections || []).map((section, sIndex) => {
        if (sIndex !== sectionIndex) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field, fIndex) => {
            if (fIndex !== fieldIndex) return field;
            return {
              ...field,
              options: (field.options || []).filter((_, optIndex) => optIndex !== optionIndex),
            };
          }),
        };
      }),
    }));
  };

  const startDrag = (item) => {
    setDragItem(item);
  };

  const endDrag = () => {
    setDragItem(null);
  };

  const allowDrop = (event) => {
    event.preventDefault();
  };

  const dropTopLevelField = (targetIndex) => {
    if (dragItem?.scope !== "top") return;
    reorderTopLevelField(dragItem.fieldIndex, targetIndex);
    setDragItem(null);
  };

  const dropSection = (targetIndex) => {
    if (dragItem?.scope !== "section") return;
    reorderSection(dragItem.sectionIndex, targetIndex);
    setDragItem(null);
  };

  const dropSectionField = (sectionIndex, targetIndex) => {
    if (dragItem?.scope !== "field" || dragItem?.sectionIndex !== sectionIndex) return;
    reorderSectionField(sectionIndex, dragItem.fieldIndex, targetIndex);
    setDragItem(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError("");
    setUploadStage("Preparing document...");
    setDraftReview(null);

    try {
      let text = null;
      let pdfLayoutItems = [];

      if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
        try {
          setUploadStage("Extracting PDF pages...");
          const arrayBuffer = await file.arrayBuffer();
          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;
          const pageTexts = [];

          for (let i = 1; i <= pdf.numPages; i += 1) {
            setUploadProgress(Math.round((i / pdf.numPages) * 100));
            setUploadStage(`Reading page ${i} of ${pdf.numPages}...`);
            const page = await pdf.getPage(i);
            const pageLayout = await extractPdfPageLayout(page);
            if (pageLayout.length > 0) {
              pdfLayoutItems.push(...pageLayout);
            }
            let pageText = await extractPdfPageText(page);
            if (!pageText) {
              pageText = await extractPdfPageWithOCR(page, i, pdf.numPages, setUploadProgress, setUploadStage);
            }
            pageTexts.push(pageText);
          }

          text = pageTexts.join("\n").trim();
        } catch (error) {
          console.error("PDF parse error:", error);
          setUploadError("Failed to extract text from PDF. Try a text-based PDF, DOCX, or TXT file, or retry after the latest app rules are deployed.");
          return;
        }
      } else if (file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || /\.docx$/i.test(file.name)) {
        try {
          setUploadStage("Extracting Word document text...");
          const arrayBuffer = await file.arrayBuffer();
          const mammothResult = await mammoth.convertToHtml({ arrayBuffer });
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = mammothResult.value;
          text = tempDiv.innerText;
        } catch (error) {
          console.error("DOCX parse error:", error);
          setUploadError("Failed to extract text from Word document. Please try another file.");
          return;
        }
      } else {
        setUploadStage("Reading document text...");
        text = await file.text();
      }

      let parsed;
      try {
        parsed = JSON.parse(text);
        if (parsed.fields && Array.isArray(parsed.fields)) {
          onDocumentUpload(parsed);
          return;
        }
      } catch (error) {
        // Not JSON, continue with AI/basic conversion.
      }

      const fallbackTitle = file.name.replace(/\.[^/.]+$/, "");
      const sourceText = String(text || "").trim();
      if (!sourceText) {
        throw new Error("No readable text was found in the uploaded document.");
      }

      const templateDraft = resolveLowTextTemplateDraft(sourceText, file.name, pdfLayoutItems);
      if (templateDraft) {
        setUploadStage("Preparing template-aware draft...");
        setDraftReview({
          draft: cloneDraftForEditing(templateDraft.draft),
          fallbackTitle,
          presetKey: templateDraft.presetKey,
          presetLabel: templateDraft.presetLabel,
          customPresetName: "",
          customPresetStarterSections: [],
          customPresetStarterFieldTemplates: [],
          sourceText,
          sourceLength: sourceText.length,
          extractionHint: templateDraft.extractionHint,
        });
        return;
      }

      let aiHandled = false;
      if (sourceText.length >= 80) {
        try {
          const companyId = await getCurrentUserCompanyId();
          if (!companyId) {
            throw new Error("No company access found for this account yet. Finish company setup and make sure the latest Firestore rules are deployed.");
          }

          setUploadProgress(100);
          setUploadStage("Generating AI draft form...");
          const generateDraft = httpsCallable(functions, "generateFormDraftFromDocument");
          const resolvedPreset = resolvePresetSelection(selectedPreset, customPresets);
          const result = await generateDraft({
            companyId,
            fileName: file.name,
            sourceText: sourceText.slice(0, 50000),
            presetKey: resolvedPreset.presetKey,
            customPresetName: resolvedPreset.customPresetName,
            customPresetInstructions: resolvedPreset.customPresetInstructions,
            customPresetStarterSections: resolvedPreset.customPresetStarterSections,
            customPresetStarterFieldTemplates: resolvedPreset.customPresetStarterFieldTemplates,
            customPresetBasePresetKey: resolvedPreset.presetKey,
          });

          const draft = result?.data?.draft;
          if (draft) {
            setUploadStage("Preparing AI review...");
            const seededDraft = mergeStarterSectionsIntoDraft(
              cloneDraftForEditing(draft),
              result?.data?.customPresetStarterSections || resolvedPreset.customPresetStarterSections || []
            );
            const seededDraftWithFields = mergeStarterFieldTemplatesIntoDraft(
              seededDraft,
              result?.data?.customPresetStarterFieldTemplates || resolvedPreset.customPresetStarterFieldTemplates || []
            );
            setDraftReview({
              draft: seededDraftWithFields,
              fallbackTitle,
              presetKey: result?.data?.presetKey || resolvedPreset.presetKey,
              presetLabel: resolvedPreset.label,
              customPresetName: result?.data?.customPresetName || resolvedPreset.customPresetName || "",
              customPresetStarterSections: result?.data?.customPresetStarterSections || resolvedPreset.customPresetStarterSections || [],
              customPresetStarterFieldTemplates: result?.data?.customPresetStarterFieldTemplates || resolvedPreset.customPresetStarterFieldTemplates || [],
              sourceText,
              sourceLength: sourceText.length,
              extractionHint: sourceText.length < 250
                ? "Only a small amount of readable PDF text was found, so review this draft carefully before opening the builder."
                : "",
            });
            aiHandled = true;
          }
        } catch (error) {
          console.error("AI form draft generation failed:", error);
          alert(`${error.message || "AI form draft generation failed."} Using basic text conversion instead.`);
        }
      }

      if (!aiHandled) {
        setUploadStage("Building basic form...");
        submitBasicDraft(fallbackTitle, sourceText);
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setUploadError("Failed to process file. Please try again or use a different file.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage("");
      e.target.value = "";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-6 backdrop-blur-sm sm:py-10"
          onClick={onClose}
        >
          <motion.div
            key="modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-3xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-8 shadow-2xl dark:border-gray-800 dark:bg-gray-900"
            style={{
              maxHeight: "calc(100vh - 3rem)",
              paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-2xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              x
            </button>

            <h2 className="mb-2 text-2xl font-bold text-gray-800 dark:text-gray-100">
              Create New Form
            </h2>

            {!hasPermission(currentUserData, "create_forms") ? (
              <div className="rounded bg-yellow-50 p-6 dark:bg-yellow-900">
                You do not have permission to create forms.
              </div>
            ) : (
              <>
                <p className="mb-6 text-gray-600 dark:text-gray-400">
                  {draftReview ? "Review the AI-generated draft before opening the form builder." : "Choose how you would like to create your form."}
                </p>

                {uploading && (
                  <div className="mb-4 w-full rounded-full bg-gray-200 h-2.5">
                    <div
                      className="h-2.5 rounded-full bg-blue-600"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}

                {!draftReview ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI import preset</p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Choose the draft style before uploading your document.
                          </p>
                          <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                            Company default: {resolvePresetSelection(companyDefaultPreset, customPresets).label || "General"}
                            {loadingPresetSettings ? " (loading...)" : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={saveCompanyDefaultPreset}
                          disabled={savingPresetSettings || !currentCompanyId || selectedPreset === companyDefaultPreset}
                          className="rounded-md border border-blue-200 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:text-blue-200"
                        >
                          {savingPresetSettings ? "Saving..." : "Set as company default"}
                        </button>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {AI_IMPORT_PRESETS.map((preset) => (
                          <button
                            key={preset.key}
                            type="button"
                            onClick={() => setSelectedPreset(preset.key)}
                            className={`rounded-lg border px-3 py-3 text-left transition-all ${
                              selectedPreset === preset.key
                                ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-100"
                                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                            }`}
                          >
                            <div className="text-sm font-semibold">{preset.label}</div>
                            <div className="mt-1 text-xs opacity-80">{preset.description}</div>
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={openPresetManagerForCreate}
                          disabled={savingPresetSettings || !currentCompanyId}
                          className="rounded-md border border-blue-200 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:text-blue-200"
                        >
                          New company preset
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            navigate("/preset-library");
                          }}
                          className="rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          Open full preset library
                        </button>
                      </div>
                      {presetManagerOpen && (
                        <div className="mt-4">
                          <FormAutomationPresetEditor
                            form={presetForm}
                            setForm={setPresetForm}
                            onCancel={closePresetManager}
                            onSave={saveCustomPreset}
                            saving={savingPresetSettings || !currentCompanyId}
                            title={editingPresetId ? "Edit company preset" : "Create company preset"}
                            saveLabel={editingPresetId ? "Save changes" : "Create preset"}
                            compact
                          />
                        </div>
                      )}
                      {customPresets.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Company presets</p>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {customPresets.map((preset) => (
                              <div
                                key={preset.id}
                                className={`rounded-lg border px-3 py-3 transition-all ${
                                  selectedPreset === `custom:${preset.id}`
                                    ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-100"
                                    : "border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => setSelectedPreset(`custom:${preset.id}`)}
                                  className="w-full text-left"
                                >
                                  <div className="text-sm font-semibold">{preset.name}</div>
                                  <div className="mt-1 text-xs opacity-80">
                                    Base: {getBuiltInPreset(preset.basePresetKey).label}
                                  </div>
                                  {Array.isArray(preset.starterSections) && preset.starterSections.length > 0 && (
                                    <div className="mt-1 text-xs opacity-80">
                                      Starter sections: {preset.starterSections.join(", ")}
                                    </div>
                                  )}
                                  {Array.isArray(preset.starterFieldTemplates) && preset.starterFieldTemplates.length > 0 && (
                                    <div className="mt-1 text-xs opacity-75">
                                      Starter fields: {preset.starterFieldTemplates.map((template) => `${template.sectionTitle} -> ${template.fields.join(", ")}`).join(" | ")}
                                    </div>
                                  )}
                                  {preset.extraInstructions && (
                                    <div className="mt-1 line-clamp-3 text-xs opacity-75">
                                      {preset.extraInstructions}
                                    </div>
                                  )}
                                </button>
                                <div className="mt-3 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => openPresetManagerForEdit(preset)}
                                    className="mr-2 rounded-md border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteCustomPreset(preset.id)}
                                    className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <motion.button
                      whileHover={{ y: -2, boxShadow: "0 10px 25px rgba(244, 63, 94, 0.15)" }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        onScratch();
                        onClose();
                      }}
                      className="w-full rounded-lg border border-rose-500/20 bg-gradient-to-r from-rose-600 to-rose-700 p-4 text-left text-white transition-all hover:from-rose-700 hover:to-rose-800"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold">+</span>
                        <div>
                          <p className="text-lg font-semibold">Start from Scratch</p>
                          <p className="text-sm text-rose-100">Build your form field by field</p>
                        </div>
                      </div>
                    </motion.button>

                    <motion.label
                      whileHover={{ y: -2, boxShadow: "0 10px 25px rgba(59, 130, 246, 0.15)" }}
                      className="block w-full cursor-pointer rounded-lg border border-blue-500/20 bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-left text-white transition-all hover:from-blue-700 hover:to-blue-800"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold">AI</span>
                        <div>
                          <p className="text-lg font-semibold">
                            {uploading ? "Processing..." : "Upload Document"}
                          </p>
                          <p className="text-sm text-blue-100">
                            {uploading
                              ? `${uploadStage || "Processing file..."} ${uploadProgress > 0 ? `${uploadProgress}%` : ""}`.trim()
                              : "Convert PDF, Word, text, or JSON into a draft form"}
                          </p>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.txt,.docx,.json,.doc"
                        onChange={handleFileSelect}
                        disabled={uploading}
                        className="hidden"
                      />
                    </motion.label>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold">AI draft ready for review</p>
                          <p className="mt-1 text-blue-800/90">
                            Check the suggested structure before sending it into the form builder.
                          </p>
                          {draftReview.extractionHint && (
                            <p className="mt-2 text-xs text-blue-800">
                              {draftReview.extractionHint}
                            </p>
                          )}
                          <p className="mt-2 text-xs text-blue-700">
                            Preset: {draftReview.presetLabel || getBuiltInPreset(draftReview.presetKey).label || "General"}
                            {draftReview.customPresetName ? ` (${draftReview.customPresetName})` : ""}
                          </p>
                          {Array.isArray(draftReview.customPresetStarterSections) && draftReview.customPresetStarterSections.length > 0 && (
                            <p className="mt-1 text-xs text-blue-700">
                              Starter sections: {draftReview.customPresetStarterSections.join(", ")}
                            </p>
                          )}
                          {Array.isArray(draftReview.customPresetStarterFieldTemplates) && draftReview.customPresetStarterFieldTemplates.length > 0 && (
                            <p className="mt-1 text-xs text-blue-700">
                              Starter fields: {draftReview.customPresetStarterFieldTemplates.map((template) => `${template.sectionTitle} -> ${template.fields.join(", ")}`).join(" | ")}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setDraftReview(null)}
                          className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                        >
                          Back
                        </button>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <input
                            type="text"
                            value={draftReview.draft?.title || ""}
                            onChange={(event) => updateDraftReview((draft) => ({ ...draft, title: event.target.value }))}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-lg font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                            placeholder={draftReview.fallbackTitle}
                          />
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            {Array.isArray(draftReview.draft?.sections) ? draftReview.draft.sections.length : 0} sections, {countDraftFields(draftReview.draft)} suggested fields
                          </p>
                        </div>
                        <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                          <div>{draftReview.sourceLength?.toLocaleString?.() || draftReview.sourceLength} characters analysed</div>
                        </div>
                      </div>

                      <textarea
                        value={draftReview.draft?.description || ""}
                        onChange={(event) => updateDraftReview((draft) => ({ ...draft, description: event.target.value }))}
                        className="mt-3 min-h-[88px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                        placeholder="Overview or introduction for this generated form"
                      />
                    </div>

                    <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
                      {Array.isArray(draftReview.draft?.standaloneFields) && draftReview.draft.standaloneFields.length > 0 && (
                        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Top-level fields</p>
                            <button
                              type="button"
                              onClick={() => addTopLevelField("text")}
                              className="rounded-md border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-200"
                            >
                              Add field
                            </button>
                          </div>
                          <div className="mt-2 space-y-2">
                            {draftReview.draft.standaloneFields.map((field, index) => (
                              <div
                                key={`standalone-${index}`}
                                draggable
                                onDragStart={() => startDrag({ scope: "top", fieldIndex: index })}
                                onDragEnd={endDrag}
                                onDragOver={allowDrop}
                                onDrop={() => dropTopLevelField(index)}
                                className={`rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-900/40 dark:text-gray-200 ${dragItem?.scope === "top" && dragItem?.fieldIndex === index ? "opacity-60 ring-2 ring-blue-300" : ""}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 space-y-2">
                                    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                      Drag to reorder
                                    </div>
                                    <input
                                      type="text"
                                      value={field.label || ""}
                                      onChange={(event) => updateTopLevelField(index, { label: event.target.value })}
                                      className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                                    />
                                    <div className="grid gap-2 sm:grid-cols-3">
                                      <select
                                        value={field.type || "text"}
                                        onChange={(event) => updateTopLevelField(index, { type: event.target.value })}
                                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                      >
                                        {["instruction", "yesno", "text", "number", "date_time", "multiple_choice", "checkbox", "media", "signature", "location"].map((type) => (
                                          <option key={type} value={type}>{getDraftTypeLabel(type)}</option>
                                        ))}
                                      </select>
                                      <select
                                        value={field.evidence || "none"}
                                        onChange={(event) => updateTopLevelField(index, { evidence: event.target.value })}
                                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                      >
                                        <option value="none">No evidence</option>
                                        <option value="photo">Photo evidence</option>
                                        <option value="signature">Signature evidence</option>
                                        <option value="photo_and_signature">Photo and signature</option>
                                      </select>
                                      <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                        <input
                                          type="checkbox"
                                          checked={!!field.required}
                                          onChange={(event) => updateTopLevelField(index, { required: event.target.checked })}
                                        />
                                        Required
                                      </label>
                                    </div>
                                    {field.type === "multiple_choice" && (
                                      <div className="rounded-md border border-blue-100 bg-blue-50/70 p-2 dark:border-blue-900/40 dark:bg-blue-950/20">
                                        <div className="mb-2 flex items-center justify-between">
                                          <span className="text-xs font-medium text-blue-900 dark:text-blue-100">Options and scores</span>
                                          <button
                                            type="button"
                                            onClick={() => addTopLevelFieldOption(index)}
                                            className="rounded border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-200"
                                          >
                                            Add option
                                          </button>
                                        </div>
                                        <div className="space-y-2">
                                          {(Array.isArray(field.options) ? field.options : []).map((option, optionIndex) => (
                                            <div key={`standalone-${index}-option-${optionIndex}`} className="grid gap-2 sm:grid-cols-[1fr_88px_auto]">
                                              <input
                                                type="text"
                                                value={option?.label || ""}
                                                onChange={(event) => updateTopLevelFieldOption(index, optionIndex, { label: event.target.value })}
                                                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                                                placeholder="Option label"
                                              />
                                              <input
                                                type="number"
                                                value={Number(option?.score ?? 0)}
                                                onChange={(event) => updateTopLevelFieldOption(index, optionIndex, { score: Number(event.target.value) || 0 })}
                                                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                                                placeholder="Score"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => removeTopLevelFieldOption(index, optionIndex)}
                                                className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeTopLevelField(index)}
                                    className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {(!Array.isArray(draftReview.draft?.standaloneFields) || draftReview.draft.standaloneFields.length === 0) && (
                        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Top-level fields</p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No standalone fields yet.</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => addTopLevelField("text")}
                              className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-200"
                            >
                              Add field
                            </button>
                          </div>
                        </div>
                      )}

                      {(Array.isArray(draftReview.draft?.sections) ? draftReview.draft.sections : []).map((section, index) => (
                        <div
                          key={`section-${index}`}
                          draggable
                          onDragStart={() => startDrag({ scope: "section", sectionIndex: index })}
                          onDragEnd={endDrag}
                          onDragOver={allowDrop}
                          onDrop={() => dropSection(index)}
                          className={`rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40 ${dragItem?.scope === "section" && dragItem?.sectionIndex === index ? "opacity-60 ring-2 ring-blue-300" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 space-y-2">
                              <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                Drag section to reorder
                              </div>
                              <input
                                type="text"
                                value={section.title || ""}
                                onChange={(event) => updateSection(index, { title: event.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                              />
                              <textarea
                                value={section.description || ""}
                                onChange={(event) => updateSection(index, { description: event.target.value })}
                                className="min-h-[72px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                                placeholder="Section guidance"
                              />
                              <div className="grid gap-2 sm:grid-cols-3">
                                <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                  <input
                                    type="checkbox"
                                    checked={!!section.repeatable}
                                    onChange={(event) => updateSection(index, { repeatable: event.target.checked })}
                                  />
                                  Repeatable
                                </label>
                                <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                  <input
                                    type="checkbox"
                                    checked={!!section.splitWorkflow}
                                    onChange={(event) => updateSection(index, { splitWorkflow: event.target.checked })}
                                  />
                                  Split workflow
                                </label>
                                <select
                                  value={section.workflowVisibility || "visible"}
                                  onChange={(event) => updateSection(index, { workflowVisibility: event.target.value })}
                                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                >
                                  <option value="visible">Previous visible</option>
                                  <option value="hidden">Previous hidden</option>
                                </select>
                              </div>
                              {section.splitWorkflow && (
                                <input
                                  type="text"
                                  value={section.assignmentHint || ""}
                                  onChange={(event) => updateSection(index, { assignmentHint: event.target.value })}
                                  className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
                                  placeholder="AI assignment suggestion, for example Manager or Cleaning Team"
                                />
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {(Array.isArray(section.fields) ? section.fields.length : 0)} fields
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSection(index)}
                                className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                              >
                                Remove section
                              </button>
                            </div>
                          </div>

                          <div className="mt-3 space-y-2">
                            {(Array.isArray(section.fields) ? section.fields : []).map((field, fieldIndex) => (
                              <div
                                key={`section-${index}-field-${fieldIndex}`}
                                draggable
                                onDragStart={() => startDrag({ scope: "field", sectionIndex: index, fieldIndex })}
                                onDragEnd={endDrag}
                                onDragOver={allowDrop}
                                onDrop={() => dropSectionField(index, fieldIndex)}
                                className={`rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-900/40 dark:text-gray-200 ${dragItem?.scope === "field" && dragItem?.sectionIndex === index && dragItem?.fieldIndex === fieldIndex ? "opacity-60 ring-2 ring-blue-300" : ""}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 space-y-2">
                                    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                      Drag field to reorder
                                    </div>
                                    <input
                                      type="text"
                                      value={field.label || ""}
                                      onChange={(event) => updateSectionField(index, fieldIndex, { label: event.target.value })}
                                      className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                                    />
                                    <div className="grid gap-2 sm:grid-cols-3">
                                      <select
                                        value={field.type || "text"}
                                        onChange={(event) => updateSectionField(index, fieldIndex, { type: event.target.value })}
                                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                      >
                                        {["instruction", "yesno", "text", "number", "date_time", "multiple_choice", "checkbox", "media", "signature", "location"].map((type) => (
                                          <option key={type} value={type}>{getDraftTypeLabel(type)}</option>
                                        ))}
                                      </select>
                                      <select
                                        value={field.evidence || "none"}
                                        onChange={(event) => updateSectionField(index, fieldIndex, { evidence: event.target.value })}
                                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                      >
                                        <option value="none">No evidence</option>
                                        <option value="photo">Photo evidence</option>
                                        <option value="signature">Signature evidence</option>
                                        <option value="photo_and_signature">Photo and signature</option>
                                      </select>
                                      <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                        <input
                                          type="checkbox"
                                          checked={!!field.required}
                                          onChange={(event) => updateSectionField(index, fieldIndex, { required: event.target.checked })}
                                        />
                                        Required
                                      </label>
                                    </div>
                                    {field.type === "multiple_choice" && (
                                      <div className="rounded-md border border-blue-100 bg-blue-50/70 p-2 dark:border-blue-900/40 dark:bg-blue-950/20">
                                        <div className="mb-2 flex items-center justify-between">
                                          <span className="text-xs font-medium text-blue-900 dark:text-blue-100">Options and scores</span>
                                          <button
                                            type="button"
                                            onClick={() => addSectionFieldOption(index, fieldIndex)}
                                            className="rounded border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-200"
                                          >
                                            Add option
                                          </button>
                                        </div>
                                        <div className="space-y-2">
                                          {(Array.isArray(field.options) ? field.options : []).map((option, optionIndex) => (
                                            <div key={`section-${index}-field-${fieldIndex}-option-${optionIndex}`} className="grid gap-2 sm:grid-cols-[1fr_88px_auto]">
                                              <input
                                                type="text"
                                                value={option?.label || ""}
                                                onChange={(event) => updateSectionFieldOption(index, fieldIndex, optionIndex, { label: event.target.value })}
                                                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                                                placeholder="Option label"
                                              />
                                              <input
                                                type="number"
                                                value={Number(option?.score ?? 0)}
                                                onChange={(event) => updateSectionFieldOption(index, fieldIndex, optionIndex, { score: Number(event.target.value) || 0 })}
                                                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                                                placeholder="Score"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => removeSectionFieldOption(index, fieldIndex, optionIndex)}
                                                className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeSectionField(index, fieldIndex)}
                                    className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-3 flex justify-end">
                            <button
                              type="button"
                              onClick={() => addSectionField(index, "text")}
                              className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-200"
                            >
                              Add field to section
                            </button>
                          </div>
                        </div>
                      ))}

                      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sections</p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Add another section if the AI draft missed part of the document.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={addSection}
                            className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-200"
                          >
                            Add section
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-3">
                      <button
                        type="button"
                        onClick={() => submitBasicDraft(draftReview.fallbackTitle, draftReview.sourceText)}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        Use basic import
                      </button>
                      <button
                        type="button"
                        onClick={() => setDraftReview(null)}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        Upload another file
                      </button>
                      <button
                        type="button"
                        onClick={submitReviewedAIDraft}
                        className="rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        Open in form builder
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {uploadError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300"
              >
                Warning: {uploadError}
              </motion.div>
            )}

            <div className="mt-6 rounded-lg bg-gray-100 p-3 text-xs text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
              <p className="mb-1 font-semibold">Supported formats:</p>
              <ul className="list-inside list-disc space-y-0.5">
                <li>JSON (exported forms)</li>
                <li>Plain text (one field per line)</li>
                <li>PDF, DOCX (AI-assisted draft generation)</li>
              </ul>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewFormModal;
